import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get("invite_code") || "";
  const organisationInviteCode = (searchParams.get("organisation_invite") || "").trim().toUpperCase();
  const sessionExpired = searchParams.get("session_expired") === "1";
  const [resetReason, setResetReason] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionExpired) return;
    try {
      const raw = sessionStorage.getItem("auth_reset_reason");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Date.now() - (parsed.at || 0) < 5 * 60 * 1000) setResetReason(parsed);
      }
    } catch {}
  }, [sessionExpired]);

  useEffect(() => {
    if (!organisationInviteCode) return undefined;
    let cancelled = false;
    axios.get(`${API_URL}/api/organisation/invites/${organisationInviteCode}/preview`)
      .then(({ data }) => {
        if (!cancelled && data?.email) setEmail(data.email);
      })
      .catch(() => {
        if (!cancelled) setError("This PDI invitation is no longer available.");
      });
    return () => { cancelled = true; };
  }, [organisationInviteCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      if (organisationInviteCode) {
        try {
          await axios.post(
            `${API_URL}/api/organisation/redeem-invite`,
            { code: organisationInviteCode },
            { withCredentials: true }
          );
          await refreshUser();
          navigate("/pricing?from=adi-invite", { replace: true });
          return;
        } catch (e) {
          setError(e?.response?.data?.detail || "We signed you in, but could not accept this PDI invitation.");
          setLoading(false);
          return;
        }
      }
      if (inviteCode) {
        try {
          await axios.post(`${API_URL}/api/pupil/auto-link`, { invite_code: inviteCode }, { withCredentials: true });
          window.location.href = "/learn";
          return;
        } catch {}
      }
      navigate("/home");
    } else {
      setError(result.error || "We couldn't sign you in. Check your details and try again.");
    }
    setLoading(false);
  };

  return (
    <main className="min-h-dvh bg-slate-950 text-white flex items-center justify-center px-5 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-9">
          <div className="mx-auto mb-5 h-16 w-16 rounded-2xl bg-white text-slate-950 flex items-center justify-center shadow-2xl">
            <span className="text-2xl font-black tracking-tight">NDG</span>
          </div>
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-slate-400">National Driving Guild</p>
          <h1 data-testid="login-title" className="mt-4 text-4xl font-black tracking-tight">Welcome back</h1>
          <p className="mt-3 text-slate-400 text-base">Sign in to continue to your workspace.</p>
        </div>

        <section className="rounded-3xl bg-white text-slate-950 p-6 sm:p-8 shadow-2xl shadow-black/30">
          {organisationInviteCode && (
            <div data-testid="login-pdi-invite" className="mb-5 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-950">
              <strong>Accept your PDI invitation</strong>
              <p className="mt-1 text-xs leading-relaxed text-indigo-800">
                Sign in with your existing NDG account. We will attach the invitation after your identity is verified.
              </p>
            </div>
          )}
          {sessionExpired && (
            <div data-testid="login-session-expired" className="mb-5 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
              <strong>Your session ended.</strong> Sign in again to continue.
              {resetReason && <span className="sr-only">{resetReason.method} {resetReason.url} {resetReason.status}</span>}
            </div>
          )}
          {error && <div data-testid="login-error" className="mb-5 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block mb-2 text-sm font-bold text-slate-700">Email address</label>
              <input id="email" data-testid="login-email-input" type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" className="w-full h-14 rounded-xl border border-slate-300 bg-slate-50 px-4 text-base outline-none transition focus:bg-white focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="text-sm font-bold text-slate-700">Password</label>
                <a href="/forgot-password" className="text-sm font-semibold text-slate-600 hover:text-slate-950">Forgot password?</a>
              </div>
              <div className="relative">
                <input id="password" data-testid="login-password-input" type={showPassword ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Enter your password" className="w-full h-14 rounded-xl border border-slate-300 bg-slate-50 px-4 pr-14 text-base outline-none transition focus:bg-white focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10" />
                <button type="button" aria-label={showPassword ? "Hide password" : "Show password"} onClick={() => setShowPassword((v) => !v)} className="absolute right-0 top-0 h-14 w-14 flex items-center justify-center text-slate-500 hover:text-slate-950">
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <button type="submit" data-testid="login-submit-btn" disabled={loading} className="w-full h-14 rounded-xl bg-slate-950 text-white text-base font-extrabold transition hover:bg-slate-800 disabled:opacity-60">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="h-4 w-4" />
            <span>Secure access · Your workspace stays private</span>
          </div>
        </section>

        <p className="mt-6 text-center text-xs leading-5 text-slate-500">Access is limited to authorised National Driving Guild accounts and invited users.</p>
      </div>
    </main>
  );
}
